import React, { useState } from 'react';
import { PaymentTubeMapResponseModel } from '../../services/api/paymentsApi';
import MxMessageModal from './MxMessageModal';

interface PaymentTimelineProps {
  data: PaymentTubeMapResponseModel[];
  paymentId: string;
}

// Format date
const formatDate = (dateString: string | null): string => {
  if (!dateString) return '-';
  
  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    return dateString;
  }
};

// Timeline node component
interface TimelineNodeProps {
  node: PaymentTubeMapResponseModel;
  isLastNode: boolean;
  paymentId: string;
}

const TimelineNode: React.FC<TimelineNodeProps> = ({ node, isLastNode, paymentId }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showMxModal, setShowMxModal] = useState(false);
  const [mxMessage, setMxMessage] = useState<string | null>(null);

  // Check if this node has an MX message
  const hasMxMessage = node.stepDetails && 
    ('Payment message' in node.stepDetails || 'Payment Message' in node.stepDetails);

  // Get the MX message
  const getMxMessage = (): string | null => {
    if (node.stepDetails) {
      return node.stepDetails['Payment message'] || 
             node.stepDetails['Payment Message'] || 
             null;
    }
    return null;
  };

  // Handle click on "View MX message" link
  const handleViewMxMessage = () => {
    const message = getMxMessage();
    if (message) {
      setMxMessage(message);
      setShowMxModal(true);
    }
  };

  // Get node status color
  const getNodeStatusColor = () => {
    if (node.isFailed) return '#f44336'; // red for failed
    if (node.isCompleted) return '#4caf50'; // green for completed
    return '#ff9800'; // orange for in progress
  };

  // Get node details as list items (excluding the message)
  const nodeDetails = Object.entries(node.stepDetails || {})
    .filter(([key]) => key !== 'Payment message' && key !== 'Payment Message')
    .map(([key, value]) => (
      <div key={key} style={{ 
        display: 'flex', 
        margin: '4px 0',
        fontSize: '14px'
      }}>
        <span style={{ 
          fontWeight: 600, 
          minWidth: '150px', 
          marginRight: '16px' 
        }}>
          {key}:
        </span>
        <span>{value}</span>
      </div>
    ));

  return (
    <>
      <div style={{
        display: 'flex',
        marginBottom: isLastNode ? 0 : '16px',
      }}>
        {/* Timeline connector */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          marginRight: '16px',
        }}>
          <div style={{
            width: '16px',
            height: '16px',
            borderRadius: '50%',
            backgroundColor: getNodeStatusColor(),
            border: '2px solid white',
            zIndex: 2,
          }} />
          {!isLastNode && (
            <div style={{
              width: '2px',
              flex: 1,
              backgroundColor: '#dee2e6',
              marginTop: '2px',
            }} />
          )}
        </div>

        {/* Node content */}
        <div style={{ flex: 1 }}>
          <div 
            style={{
              backgroundColor: 'white',
              borderRadius: '4px',
              padding: '16px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              cursor: 'pointer',
              borderLeft: `4px solid ${getNodeStatusColor()}`,
            }}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: isExpanded ? '16px' : 0
            }}>
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '16px' }}>
                  {node.stepName}
                </div>
                <div style={{ color: '#6c757d', fontSize: '14px' }}>
                  {formatDate(node.statusTime)}
                </div>
              </div>
              <div>
                {isExpanded ? '▲' : '▼'}
              </div>
            </div>

            {isExpanded && (
              <div style={{ marginTop: '8px' }}>
                {nodeDetails}
                
                {hasMxMessage && (
                  <div style={{ marginTop: '16px' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewMxMessage();
                      }}
                      style={{
                        backgroundColor: '#003366',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '8px 12px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      View MX Message
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Render child nodes (if any and expanded) */}
          {isExpanded && node.children && node.children.length > 0 && (
            <div style={{ marginLeft: '16px', marginTop: '16px' }}>
              {node.children.map((childNode, index) => (
                <TimelineNode
                  key={childNode.eventId}
                  node={childNode}
                  isLastNode={index === node.children!.length - 1}
                  paymentId={paymentId}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* MX Message Modal */}
      {showMxModal && mxMessage && (
        <MxMessageModal
          message={mxMessage}
          paymentId={paymentId}
          onClose={() => setShowMxModal(false)}
        />
      )}
    </>
  );
};

const PaymentTimeline: React.FC<PaymentTimelineProps> = ({ data, paymentId }) => {
  return (
    <div style={{ padding: '24px' }}>
      <h2 style={{ marginBottom: '24px' }}>Payment Timeline</h2>
      
      <div style={{ 
        backgroundColor: '#f8f9fa', 
        padding: '24px',
        borderRadius: '4px',
      }}>
        {data.map((node, index) => (
          <TimelineNode
            key={node.eventId}
            node={node}
            isLastNode={index === data.length - 1}
            paymentId={paymentId}
          />
        ))}
      </div>
    </div>
  );
};

export default PaymentTimeline; 